import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, switchMap} from 'rxjs/operators';
import { of } from 'rxjs';
import * as orgActions from '../actions/organization.actions';
import * as authActions from '../actions/auth.actions';
import * as userActions from '../actions/user.actions';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthEffects {
    initAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.AuthActionTypes.initAuth),
            switchMap(() => {
                const token = localStorage.getItem('token');
                if (token !== null) {
                    return [authActions.SignInSuccess({ token }), userActions.InitUser()];
                }
                return of(authActions.InitAuthFailure());
            })
        )
    );

    signIn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.AuthActionTypes.signIn),
            switchMap((action: { idNumber: string; password: string  }) =>
                this.authService.signIn(action.idNumber, action.password).pipe(
                    switchMap((res: {
                        accessToken: string;
                        refreshToken: string;
                        user: any;
                    }) => {
                        if (res.accessToken !== undefined) {
                            return [
                                userActions.GetUserSuccess(res.user),
                                authActions.SignInSuccess({ token: res.accessToken }),
                            ];
                        }
                        return of(authActions.SignInSuccess(res));
                    }),
                    catchError((error: any) => of(authActions.SignInFailure(error)))
                )
            )
        )
    );

    register$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.AuthActionTypes.register),
            exhaustMap(
                (action: {
                    email: string;
                    password: string;
                    confirmPassword: string;
                    organization: string;
                    organizationName: string;
                }) =>
                    this.authService.signUp(action).pipe(
                        switchMap((res: any) => {
                            if (res.accessToken !== undefined) {
                                return [
                                    userActions.GetUserSuccess(res.userAccount),
                                    authActions.SignInSuccess({ token: res.accessToken }),
                                    authActions.RegisterSuccess({ isLoggedIn: true }),
                                    orgActions.GetOrganizationSuccess({
                                        organization: res.organization,
                                        organizationAccount: res.account,
                                    }),
                                ];
                            }
                            return of(authActions.SignInSuccess(res), authActions.RegisterSuccess(res));
                        }),
                        catchError((error: any) => of(authActions.RegisterFailure(error)))
                    )
            )
        )
    );

    forgotPassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.AuthActionTypes.forgotPassword),
            switchMap((action: { email: string }) =>
                this.authService.forgotPassword(action.email).pipe(
                    switchMap((res: any) => {
                        if (res.DATA) {
                            return of(authActions.ForgotPasswordSuccess());
                        }
                        return of(authActions.ForgotPasswordFailure());
                    }),
                    catchError(() => of(authActions.ForgotPasswordFailure()))
                )
            )
        )
    );
    signOut$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.AuthActionTypes.signOut),
            switchMap((action: { email: string }) => {
                localStorage.clear();
                this.router.navigate(['']);
                return [
                    userActions.ClearUser(),
                    orgActions.ClearOrganization(),
                ];
            })
        )
    );

    constructor(
        public toastService: ToastrService,
        private actions$: Actions,
        private authService: AuthService,
        private router: Router,
    ) {}
}
